import { TenantService, DataService } from "../../Services";
import Constant, { selectedPolygonTypes } from "./Constants";
import Functions from "./Functions";
import has from "lodash/has";
import pick from "lodash/pick";
import { groupBy, mapValues, omit } from "lodash";
import { setMyConfig } from "../../Services/auth";
import userServices from "../../UserServices/UserServices";
import { havePermission } from "../../permissions";
import LAYER_PERMISSIONS from "../../permissions/Layers.json";
import * as polyline from "polyline";

// 1. Pick a sensible max-cache size (e.g. 5 MiB)
const MAX_CACHE_SIZE = 5 * 1024 * 1024; // bytes

/* shorthand keys used by the backend */
const K = {
	name: "n",
	type: "t",
	id: "i",
	sub: "s",
	parent: "p",
	boundary: "b",
	rings: "r",
	show: "o",
	center: "c"
};

/* helper: polyline string  ➜  [[lng,lat]…] */
const decodeRing = str => polyline.decode(str, 5).map(([lat, lng]) => [lng, lat]);

/**
 * @param {Array<Object>} compressed – array of 1-letter keyed objects
 * @returns {Array<Object>} – full objects ready for Leaflet / Mapbox
 */
export function inflateAreas(compressed) {
	return compressed.map(item => {
		const raw = {
			name: item[K.name],
			type: item[K.type]
		};

		if (item[K.id]) raw._id = item[K.id];
		if (item[K.sub]) raw.sub_areas = item[K.sub];
		if (item[K.parent]) raw.parent_area = item[K.parent];

		/* boundary */
		if (item[K.boundary]) {
			const b = item[K.boundary];
			raw.boundary = {
				type: "Polygon",
				coordinates: b[K.rings].map(decodeRing),
				showInfo: b[K.show] === 0 ? false : true,
				style: {} // backend stripped it, put default back
			};
		}

		/* center */
		if (item[K.center]) {
			raw.center = { type: "Point", coordinates: item[K.center] };
		}

		return raw;
	});
}

const {
	generatePolygons,

	variables,
	Tenant: { PCI_BOTTLER, ISMAIL_INDUSTRIES },
	defaultFilters
} = Constant;
const { clientShops, censusShops, filteredShops, bat_contracted_outlets } = variables;
const { Error, getPolygon } = Functions;
//
const { defaultpolygons, categoryWhiteSpace, TargetedConsumer, PopulationGrids } = selectedPolygonTypes;

const fetchAllPolygonData = async ({ tenant, dispatch }) => {
	try {
		let data = await DataService.fetchDataFromS3(tenant, "ALL", "All_Polygon_Data");

		data = mapValues(data, ob => omit(ob, "coordinates"));

		if (tenant === PCI_BOTTLER) data = mapValues(data, ob => pick(ob, "population", "sec"));
		if (tenant === ISMAIL_INDUSTRIES)
			data = mapValues(data, ob => pick(ob, "population", "sec", "FMCG", "Category"));

		if (data) {
			dispatch({
				type: "setObject",
				actions: {
					set: {
						[`loadingsData.StatsData`]: false,
						[`allPolygons.ShowStatsData`]: data
					}
				}
			});

			dispatch({
				type: "UPDATETABLEDATA"
			});
		}
	} catch (error) {
		Error("Error While Getting Basic Info File.");
	}
};

const fetchCount = async function fetchCount({ type, data, dispatch }) {
	dispatch({
		type: "setObject",
		actions: {
			set: {
				[`tableData.${censusShops}`]: "LOADING"
			}
		}
	});
	const { data: shopCounts } = await DataService.fetchCensusCount({ selected: data, type });
	dispatch({
		type: "setObject",
		actions: {
			set: {
				[`loadingsData.${censusShops}.${type}`]: false,
				[`loadingsData.${filteredShops}.${type}`]: false,
				[`tableData.${censusShops}`]: 0
			},
			merge: {
				shopCounts
			}
		}
	});
	dispatch({
		type: "UPDATETABLEDATA"
	});
};
const fetchOutletCount = async function fetchCount({ type, dispatch }) {
	dispatch({
		type: "setObject",
		actions: {
			set: {
				[`tableData.${clientShops}`]: "LOADING"
			}
		}
	});
	const { data: resultdata } = await DataService.fetchOutletCount({
		type
	});

	let shopCounts = {};
	Object.entries(resultdata).forEach(([key, value]) => {
		shopCounts[`${key}-Client`] = value;
	});
	dispatch({
		type: "setObject",
		actions: {
			set: {
				[`loadingsData.${clientShops}.${type}`]: false,
				[`tableData.${clientShops}`]: 0
			},
			merge: {
				shopCounts
			}
		}
	});
	dispatch({
		type: "UPDATETABLEDATA"
	});
};

const fetchOutletCountSpecificFilters = async function fetchCount({ type, dispatch, tenant }) {
	let Filters = {};
	if (has(defaultFilters, `${tenant}-${variables.bat_contracted_outlets}`)) {
		Filters = {
			...Filters,
			...defaultFilters[`${tenant}-${variables.bat_contracted_outlets}`]
		};
	}

	const { data: resultdata } = await DataService.fetchOutletCount({
		type,
		Filters,
		tenant
	});

	let shopCounts = {};
	Object.entries(resultdata).forEach(([key, value]) => {
		shopCounts[`${key}-${bat_contracted_outlets}`] = value;
	});
	dispatch({
		type: "setObject",
		actions: {
			set: {
				[`loadingsData.${bat_contracted_outlets}.${type}`]: false
			},
			merge: {
				shopCounts
			}
		}
	});
	dispatch({
		type: "UPDATETABLEDATA"
	});
};

const dataConfig = {
	initialRequests: {
		// sendInitialRequestForMarkers: setCensusShopMarkersCache,
	},
	count: [fetchCount, 0],
	Outletcount: [fetchOutletCount, 1000],
	OutletcountSpecificFilters: [fetchOutletCountSpecificFilters, 2000]
	// data: [fetchShopsData, 0]
};

// Cache for fetched data; keys are constructed as "tenant-geo-fileName"
const fetchedDataCache = {};

// /**
//  * Fetches extra data from S3 and generates polygons.
//  * If the same file is requested again (for the same tenant and geo),
//  * it uses the cached data.
//  *
//  * @param {string} geo - The geographical identifier.
//  * @param {string} tenant - The tenant identifier.
//  * @param {function} dispatch - The dispatch function for updating state.
//  * @param {string} fileName - The S3 file name to fetch.
//  * @param {string} fieldName - The key used to generate polygons.
//  */
// async function fetchExtraData(geo, tenant, dispatch, fileName, fieldName) {
//   // Construct a cache key for the file.
//   const cacheKey = `${tenant}-${geo}-${fileName}`;

//   try {
//     // Set loading state.
//     dispatch({
//       type: "setObject",
//       actions: {
//         set: {
//           [`loadingsData.${geo}-${fieldName}`]: true
//         }
//       }
//     });

//     // Use cached data if available.
//     let data;
//     if (fetchedDataCache[cacheKey]) {
//       data = fetchedDataCache[cacheKey];
//     } else {
//       data = await DataService.fetchDataFromS3(tenant, geo, fileName);
//       fetchedDataCache[cacheKey] = data;
//     }

//     // Special case: for "whitespace_data", if the user has category permission,
//     // fetch the category file and generate polygons for the category.
//     if (fileName === "whitespace_data" && havePermission(LAYER_PERMISSIONS.LAYER_CATEGORY_WHITE_SPACE)) {
//       const categoryFileName = `category_${fileName}`;
//       const categoryCacheKey = `${tenant}-${geo}-${categoryFileName}`;
//       let categoryData;
//       if (fetchedDataCache[categoryCacheKey]) {
//         categoryData = fetchedDataCache[categoryCacheKey];
//       } else {
//         categoryData = await DataService.fetchDataFromS3(tenant, geo, categoryFileName);
//         fetchedDataCache[categoryCacheKey] = categoryData;
//       }
//       generatePolygons[categoryWhiteSpace]({ data: categoryData, categoryWhiteSpace, dispatch, tenant, geo });
//     }

//     // Special case: for "population_density" when fieldName equals PopulationGrids and permission exists,
//     // fetch the persona file and generate polygons for the targeted consumer.
//     if (
//       fileName === "population_density" &&
//       fieldName === PopulationGrids &&
//       havePermission(LAYER_PERMISSIONS.LAYER_TRAGETED_CONSUMER)
//     ) {
//       const personaFileName = `persona_population_density`;
//       const personaCacheKey = `${tenant}-${geo}-${personaFileName}`;
//       let personaData;
//       if (fetchedDataCache[personaCacheKey]) {
//         personaData = fetchedDataCache[personaCacheKey];
//       } else {
//         personaData = await DataService.fetchDataFromS3(tenant, geo, personaFileName);
//         fetchedDataCache[personaCacheKey] = personaData;
//       }
//       generatePolygons[TargetedConsumer]({ data: personaData, TargetedConsumer, dispatch, tenant, geo });
//     }

//     // Finally, generate polygons for the given fieldName using the (cached or fetched) data.
//     generatePolygons[fieldName]({ data, fieldName, dispatch, tenant, geo });
//   } catch (err) {
//     // On error, clear the loading state and log the error.
//     dispatch({
//       type: "setObject",
//       actions: {
//         set: {
//           [`loadingsData.${geo}-${fieldName}`]: undefined
//         }
//       }
//     });
//     Error(`Data Not Available For ${fieldName}`);
//     console.error({ err });
//   }
// }

const fetchData = {
	fetchExtraData: async function (geo, tenant, dispatch, fileName, fieldName) {
		// Construct a cache key for the file.
		const cacheKey = `${tenant}-${geo}-${fileName}`;

		try {
			// Set loading state.
			dispatch({
				type: "setObject",
				actions: {
					set: {
						[`loadingsData.${geo}-${fieldName}`]: true
					}
				}
			});

			// Use cached data if available.
			let data;
			if (fetchedDataCache[cacheKey]) {
				data = fetchedDataCache[cacheKey];
			} else {
				data = await DataService.fetchDataFromS3(tenant, geo, fileName);

				// 2. serialize to string and measure its byte length
				const jsonString = JSON.stringify(data);
				// option A: via Blob API
				const sizeInBytes = new Blob([jsonString]).size;
				// option B: via TextEncoder
				// const sizeInBytes = new TextEncoder().encode(jsonString).length;

				if (sizeInBytes <= MAX_CACHE_SIZE) {
					fetchedDataCache[cacheKey] = data;
				} else {
					console.warn(
						`⚠️ Skipping cache for “${fileName}” – ${sizeInBytes} bytes exceeds ${MAX_CACHE_SIZE} byte limit`
					);
				}
			}

			// Special case: for "whitespace_data", if the user has category permission,
			// fetch the category file and generate polygons for the category.
			if (fileName === "whitespace_data" && havePermission(LAYER_PERMISSIONS.LAYER_CATEGORY_WHITE_SPACE)) {
				const categoryFileName = `category_${fileName}`;
				const categoryCacheKey = `${tenant}-${geo}-${categoryFileName}`;
				let categoryData;
				if (fetchedDataCache[categoryCacheKey]) {
					categoryData = fetchedDataCache[categoryCacheKey];
				} else {
					categoryData = await DataService.fetchDataFromS3(tenant, geo, categoryFileName);
					fetchedDataCache[categoryCacheKey] = categoryData;
				}
				generatePolygons[categoryWhiteSpace]({ data: categoryData, categoryWhiteSpace, dispatch, tenant, geo });
			}

			// Special case: for "population_density" when fieldName equals PopulationGrids and permission exists,
			// fetch the persona file and generate polygons for the targeted consumer.
			if (
				fileName === "population_density" &&
				fieldName === PopulationGrids &&
				havePermission(LAYER_PERMISSIONS.LAYER_TRAGETED_CONSUMER)
			) {
				const personaFileName = `persona_population_density`;
				const personaCacheKey = `${tenant}-${geo}-${personaFileName}`;
				let personaData;
				if (fetchedDataCache[personaCacheKey]) {
					personaData = fetchedDataCache[personaCacheKey];
				} else {
					personaData = await DataService.fetchDataFromS3(tenant, geo, personaFileName);
					fetchedDataCache[personaCacheKey] = personaData;
				}
				generatePolygons[TargetedConsumer]({ data: personaData, TargetedConsumer, dispatch, tenant, geo });
			}

			// Finally, generate polygons for the given fieldName using the (cached or fetched) data.
			generatePolygons[fieldName]({ data, fieldName, dispatch, tenant, geo });
		} catch (err) {
			// On error, clear the loading state and log the error.
			dispatch({
				type: "setObject",
				actions: {
					set: {
						[`loadingsData.${geo}-${fieldName}`]: undefined
					}
				}
			});
			Error(`Data Not Available For ${fieldName}`);
			console.error({ err });
		}
	},
	getUserPolygons: async function fetchData({ dispatch, handleSelection, selectedRoute }) {
		try {
			const { tenant } = pick(userServices.getCurrentUser(), ["tenant", "isSuperAdmin"]);
			dispatch({
				type: "setObject",
				actions: {
					set: {
						"apiTracking.current": 1,
						"apiTracking.status": "wait",
						[`apiTracking.${tenant}`]: true
					}
				}
			});
			const {
				data: { polygons, types, selected }
			} = await DataService.getUserPolygons({
				preferredId: selectedRoute,
				onDownloadProgress: progressEvent => {
					const {
						loaded,
						total,
						lengthComputable,
						currentTarget: { status }
					} = progressEvent;
					if (status === 200) {
						dispatch({
							type: "setObject",
							actions: {
								set: {
									"apiTracking.current": 2,
									"apiTracking.status": "process"
								}
							}
						});
						if (lengthComputable) {
							const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
							if (loaded === total) {
								dispatch({
									type: "setObject",
									actions: {
										set: {
											// [`apiTracking.${tenant}`]: "Completed",
											"apiTracking.current": 3
										}
									}
								});
							} else
								dispatch({
									type: "setObject",
									actions: {
										set: {
											[`apiTracking.${tenant}-Downloading`]: percent
										}
									}
								});
						}
					}
				}
			});

			const reversePolygons = inflateAreas(polygons);
			const groupedPolygons = groupBy(reversePolygons, "type");
			// dispatch({
			// 	type: "setObject",
			// 	actions: {
			// 		set: {
			// 			[`apiTracking.${tenant}`]: false
			// 		}
			// 	}
			// });

			let allPolygons = {};
			let keyNameMapping = {};
			let areasMapping = new Map();
			let structure = new Array(types.length);
			types.forEach((type, index) => {
				structure[index] = groupedPolygons[type];
			});

			for (let [, typeData] of Object.entries(groupedPolygons)) {
				for (let {
					_id,
					name,
					boundary: { coordinates },
					center: { coordinates: center },
					type,
					sub_areas = [],
					parent_area
				} of typeData) {
					let polygon = getPolygon({ _id, coordinates, center, type, sub_areas, parent_area, name });
					allPolygons[_id] = polygon;
					if (sub_areas?.length > 0) {
						areasMapping.set(_id, sub_areas);
					}
					keyNameMapping[_id] = name;
				}
			}

			const { data } = await TenantService.getMyConfig();
			setMyConfig(
				data?.permissions || [],
				data?.censusReportColumns || [],
				data?.clientReportColumns || [],
				data?.filters || [],
				data?.censusShopCardData || [],
				data?.clientShopCardData || [],
				data?.secDifination || []
			);

			dispatch({
				type: "setObject",
				actions: {
					set: {
						"apiTracking.current": 3,
						[`apiTracking.${tenant}`]: false
					}
				}
			});

			console.log({ selectedRoute, selected });
			dispatch({
				type: "setObject",
				actions: {
					set: {
						[`allPolygons.${defaultpolygons}`]: allPolygons,
						selectedType: types[0],
						keyNameMapping,
						areasMapping,
						dropdownData: JSON.parse(JSON.stringify(structure)),
						dropdownDataCopy: JSON.parse(JSON.stringify(structure)),
						selecteddropDown: [selected],
						dropdownMapping: types
					}
				}
			});

			handleSelection({
				value: selected,
				dropdownDataCopy: JSON.parse(JSON.stringify(structure)),
				index: 0,
				allPolygons: { [defaultpolygons]: allPolygons },
				dispatch
			});

			if (has(dataConfig, `initialRequests`)) {
				for (let [, value] of Object.entries(dataConfig["initialRequests"])) {
					value({ types, tenant });
				}
			}
			fetchAllPolygonData({ tenant, dispatch });

			let keyMaping = {};
			for (let [key, value] of Object.entries(structure)) {
				keyMaping[key] = value.map(({ _id }) => _id);
			}

			for (let [key, value] of Object.entries(dataConfig)) {
				if (!["initialRequests"].includes(key)) {
					const [fetch, timeout] = value;
					setTimeout(() => {
						for (let type of types) {
							fetch({ type, dispatch, data: keyMaping[type], keyNameMapping, tenant });
						}
					}, timeout);
				}
			}
		} catch (error) {
			Error(error.message);
			dispatch({
				type: "setObject",
				actions: {
					set: {
						"apiTracking.status": "error",
						"apiTracking.error": error.message
					}
				}
			});
		}
	}
};
export default fetchData;
